import { useCallback, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { Grid, GridItem } from '@optimus-web/core';
import { LocalizedCurrencyValue, lt } from '@mews/framework';
import { UnicodeEntity } from '@mews/enums';
import {
    getCurrentEnterpriseAcceptedCurrencyCodes,
    getCurrentEnterpriseDefaultCurrencyCode,
} from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { useCommanderSelector } from '@mews-commander/utils';
import { useDefaultFormContext } from '../forms/ProcessPayment/useDefaultFormValues';
import {
    CommonProcessPaymentFormModel,
    ProcessPaymentFormField,
} from '../forms/ProcessPayment/ProcessPaymentFormSchema';
import { useConvertAmountToCurrencySuspense } from '../../hooks/useConvertAmountToCurrencySuspense';
import { PaymentAmountInputField } from './PaymentAmountInputField';
import { CurrencySelectField } from './CurrencySelectField';

interface PaymentAmountSectionProps {
    enabledCurrencies?: string[];
    isCurrencyFieldDisabled?: boolean;
}

export const PaymentAmountSection = ({ enabledCurrencies, isCurrencyFieldDisabled }: PaymentAmountSectionProps) => {
    const {
        defaultFormValues: { currencyCode },
        billGrossAmount,
        setSelectedCurrencyCode,
        setSelectedAmount,
        canLinkInvoicesConfig,
    } = useDefaultFormContext();
    const { setValue, getValues } = useFormContext<CommonProcessPaymentFormModel>();
    const convertAmountToCurrency = useConvertAmountToCurrencySuspense();

    const currencies = useCommanderSelector<string[]>(
        (state) => enabledCurrencies ?? getCurrentEnterpriseAcceptedCurrencyCodes(state)
    );
    const isSingleCurrency = currencies.length === 1;
    const handleCurrencyChange = useCallback(
        (newCurrency: string) => {
            if (canLinkInvoicesConfig) {
                return;
            }

            const convertedAmount = convertAmountToCurrency(billGrossAmount || 0, newCurrency) || 0;

            const result = convertedAmount && convertedAmount !== 0 ? convertedAmount : '';

            // @ts-expect-error string is not assignable to type number
            setValue(ProcessPaymentFormField.Amount, result);
            setSelectedAmount(convertedAmount);
        },
        [billGrossAmount, setSelectedAmount, setValue, convertAmountToCurrency, canLinkInvoicesConfig]
    );

    useEffect(() => {
        if (!enabledCurrencies?.includes(currencyCode)) {
            const currentCurrencyCode = getValues(ProcessPaymentFormField.CurrencyCode);
            setSelectedCurrencyCode(currentCurrencyCode);
            handleCurrencyChange(currentCurrencyCode);
        }
    }, [handleCurrencyChange, enabledCurrencies, currencyCode, getValues, setSelectedCurrencyCode]);

    return (
        <Grid columnGap="100" templateColumns="0.65fr 0.35fr" largePhoneMax={{ templateColumns: '1fr' }}>
            <GridItem>{canLinkInvoicesConfig ? <InvoiceAmountField /> : <PaymentAmountField />}</GridItem>
            <GridItem>
                <CurrencySelectField
                    currencies={currencies}
                    onChange={(_, s) => {
                        handleCurrencyChange(s as string);
                        setSelectedCurrencyCode(s as string);
                    }}
                    readOnly={isCurrencyFieldDisabled || isSingleCurrency}
                />
            </GridItem>
        </Grid>
    );
};

const InvoiceAmountField = () => {
    const { setSelectedAmount, invoiceTotal } = useDefaultFormContext();

    const { watch, setValue } = useFormContext<CommonProcessPaymentFormModel>();

    const currencyCode = watch(ProcessPaymentFormField.CurrencyCode);
    const selectedInvoiceIds = watch(ProcessPaymentFormField.InvoiceIds);

    const hasSelectedInvoices = (selectedInvoiceIds?.length || 0) > 0;

    useEffect(() => {
        if (invoiceTotal == null) return;

        setSelectedAmount(invoiceTotal);
        setValue(ProcessPaymentFormField.Amount, invoiceTotal);
    }, [invoiceTotal, setSelectedAmount, setValue]);

    return (
        <PaymentAmountInputField
            name={ProcessPaymentFormField.Amount}
            currencyCode={currencyCode}
            onChange={(_, v) => setSelectedAmount(v as number)}
            helper={
                hasSelectedInvoices
                    ? lt('SelectInvoicesToLinkPaymentAmountFieldLabel', {
                          invoicesTotal: (
                              <LocalizedCurrencyValue
                                  value={{
                                      currency: currencyCode,
                                      value: invoiceTotal || 0,
                                  }}
                              />
                          ),
                      })
                    : null
            }
        />
    );
};

const PaymentAmountField = () => {
    const { billGrossAmount, setSelectedAmount } = useDefaultFormContext();
    const defaultCurrencyCode = useCommanderSelector(getCurrentEnterpriseDefaultCurrencyCode);
    const { watch } = useFormContext<CommonProcessPaymentFormModel>();

    const currencyCode = watch(ProcessPaymentFormField.CurrencyCode);

    return (
        <PaymentAmountInputField
            name={ProcessPaymentFormField.Amount}
            currencyCode={currencyCode}
            onChange={(_, v) => setSelectedAmount(v as number)}
            helper={
                billGrossAmount &&
                !!defaultCurrencyCode && (
                    <>
                        {lt('BillTotal')}
                        {UnicodeEntity.NoBreakSpace}
                        <LocalizedCurrencyValue
                            value={{
                                currency: defaultCurrencyCode,
                                value: billGrossAmount,
                            }}
                        />
                    </>
                )
            }
        />
    );
};
